/**
 * AppContext - Manages global application UI and selection state.
 *
 * This context holds state that is shared across many components but is
 * not related to authentication or offline/sync concerns. It tracks:
 *	 - The currently selected company (multi-tenant support)
 *	 - The currently selected location/building (for filtering work orders, etc.)
 *	 - Sidebar open/closed state (for responsive layout toggling)
 *
 * Usage: Wrap your app in <AppProvider> (inside <AuthProvider>), then
 * access the values via useContext(AppContext) or a dedicated useApp() hook.
 */
import React, { createContext, useState, useCallback } from 'react';

/**
 * AppContextValue - Shape of the value exposed by AppContext.
 *
 * @property currentCompanyId	  - ID of the active company tenant, or null if none selected.
 * @property setCurrentCompanyId  - Setter to switch the active company.
 * @property selectedLocationId	  - ID of the selected building/location for data filtering.
 * @property setSelectedLocationId - Setter to change the active location filter.
 * @property sidebarOpen		  - Whether the sidebar navigation is currently expanded.
 * @property toggleSidebar		  - Toggles the sidebar between open and closed.
 * @property setSidebarOpen		  - Directly sets the sidebar open/closed state.
 */
interface AppContextValue {
	currentCompanyId: string | null;
	setCurrentCompanyId: (id: string | null) => void;
	selectedLocationId: string | null;
	setSelectedLocationId: (id: string | null) => void;
	sidebarOpen: boolean;
	toggleSidebar: () => void;
	setSidebarOpen: (open: boolean) => void;
}

// Create the context with undefined default so we can detect missing providers
export const AppContext = createContext<AppContextValue | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	// --- Tenant / company selection ---
	// Tracks which company the user is currently operating within (multi-tenant)
	const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(null);

	// --- Location filter ---
	// Tracks which building/location is selected for filtering lists of
	// work orders, inspections, etc.
	const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

	// --- Sidebar UI state ---
	// Defaults to open (true); on mobile, components may call setSidebarOpen(false)
	const [sidebarOpen, setSidebarOpen] = useState(true);

	// Memoized toggle to avoid unnecessary re-renders in consumers
	const toggleSidebar = useCallback(() => {
		setSidebarOpen((prev) => !prev);
	}, []);

	return (
		<AppContext.Provider value={{ currentCompanyId, setCurrentCompanyId, selectedLocationId, setSelectedLocationId, sidebarOpen, toggleSidebar, setSidebarOpen }}>
			{children}
		</AppContext.Provider>
	);
};
